import { queryOptions } from "@tanstack/react-query";
import {
  getShopifyAdminStatus,
  listResearchProducts,
  listResearchProductsAdmin,
} from "./store.functions";

export type {
  ArchiveProductResult,
  AttachProductFileResult,
  CartLine,
  CartPayload,
  CheckProductFileResult,
  DetachProductFileResult,
  ProductFileKind,
  ProductStatus,
  ResearchProduct,
  ResearchProductAdmin,
  SaveProductInput,
  SaveProductResult,
  ShopifyAdminStatus,
  SyncProductsResult,
} from "./store.functions";

export const researchProductsQueryOptions = () =>
  queryOptions({
    queryKey: ["research_products"],
    queryFn: () => listResearchProducts(),
  });

export const researchProductsAdminQueryOptions = () =>
  queryOptions({
    queryKey: ["research_products", "admin"],
    queryFn: () => listResearchProductsAdmin(),
  });

/**
 * Whether the Shopify Admin token is in place. Cheap and rarely changes, so
 * the admin screen holds it for a minute rather than probing on every render.
 */
export const shopifyAdminStatusQueryOptions = () =>
  queryOptions({
    queryKey: ["shopify-admin-status"],
    queryFn: () => getShopifyAdminStatus(),
    staleTime: 60_000,
  });

/**
 * Display-only cleanup for a product detail line. The STORED strings carry
 * their own leading "• " (the old miinews.com site renders them raw and
 * depends on that), so the data must never be rewritten — but our renderers
 * use real <ul> markers, which made the baked-in bullet show as a double
 * bullet. Strip only the leading bullet glyphs at render time.
 */
export function cleanDetail(detail: string): string {
  return detail.replace(/^[\s•·]+/, "");
}

/**
 * Publish dates render as Month + Year only, everywhere (customer decision,
 * 2026-09-01). The stored value stays a full date string (the editor writes
 * the first of the month) because Shopify's metafield is free text with an
 * inconsistent legacy catalog and the cache sorts it as a string.
 *
 * Parsing note: "YYYY-MM-DD" through Date.parse() is UTC midnight, which
 * toLocaleDateString would render as the PREVIOUS month for day-01 values in
 * US timezones — so year/month are extracted directly and built as a local
 * date. Unparseable legacy text renders as stored.
 */
export function formatPublishDate(
  value: string | null | undefined,
  style: "long" | "short" = "short",
): string {
  if (!value) return "";
  const asMonthYear = (y: number, m: number) =>
    new Date(y, m - 1, 1).toLocaleDateString("en-US", { month: style, year: "numeric" });
  const parts = /^(\d{4})-(\d{1,2})/.exec(value.trim());
  if (parts) {
    const month = Number(parts[2]);
    if (month >= 1 && month <= 12) return asMonthYear(Number(parts[1]), month);
  }
  const t = Date.parse(value);
  if (!Number.isNaN(t)) {
    const d = new Date(t);
    return asMonthYear(d.getFullYear(), d.getMonth() + 1);
  }
  return value;
}

const MONTHS = [
  "january",
  "february",
  "march",
  "april",
  "may",
  "june",
  "july",
  "august",
  "september",
  "october",
  "november",
  "december",
];

/** "Dec", "Dec.", "December", "Sept" → 1-12; anything else → 0. */
function monthFromName(word: string): number {
  const w = word.toLowerCase().replace(/\.$/, "");
  if (w.length < 3) return 0;
  const i = MONTHS.findIndex((m) => m.startsWith(w));
  return i + 1;
}

/**
 * Publish date as a sortable number (UTC midnight), or null when the text
 * can't be read. The metafield is free text, so the formats seen in the
 * catalog are parsed explicitly rather than trusted to Date.parse (Chrome
 * rejects "Dec. 2025" and "12/2025"): "YYYY", "YYYY-MM[-DD]", "M/YYYY",
 * "M/D/YYYY", "December 2025", "Dec 2025", "Dec. 2025", "December 1, 2025".
 * Missing day = the 1st. Date.parse is only a last resort, read as a local
 * date so it ties with the ISO form of the same day.
 */
export function publishTime(value: string | null | undefined): number | null {
  const s = String(value ?? "").trim().replace(/[.,;\s]+$/, "");
  if (!s) return null;
  const at = (y: number, m: number, d = 1) =>
    y >= 1900 && y <= 2999 && m >= 1 && m <= 12 && d >= 1 && d <= 31
      ? Date.UTC(y, m - 1, d)
      : null;

  if (/^\d{4}$/.test(s)) return at(Number(s), 1);
  let p = /^(\d{4})-(\d{1,2})(?:-(\d{1,2}))?/.exec(s);
  if (p) return at(Number(p[1]), Number(p[2]), p[3] ? Number(p[3]) : 1);
  p = /^(\d{1,2})\/(\d{4})$/.exec(s);
  if (p) return at(Number(p[2]), Number(p[1]));
  p = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(s);
  if (p) return at(Number(p[3]), Number(p[1]), Number(p[2]));
  p = /^([A-Za-z]+\.?)\s+(?:(\d{1,2})(?:st|nd|rd|th)?,?\s+)?(\d{4})$/.exec(s);
  if (p) {
    const month = monthFromName(p[1]);
    // Not a month word: unreadable (V8's Date.parse would guess "Decx" = Dec).
    return month ? at(Number(p[3]), month, p[2] ? Number(p[2]) : 1) : null;
  }

  const t = Date.parse(s);
  if (Number.isNaN(t)) return null;
  const d = new Date(t);
  return Date.UTC(d.getFullYear(), d.getMonth(), d.getDate());
}

/**
 * Public order for research studies: strictly newest publish date first
 * (owner decision 2026-10-16; display_order no longer overrides it), blank
 * or unreadable dates last, ties by title A to Z. The API's SQL order is
 * not relied on, since publish_date is free text and sorts as a string there.
 */
export function sortByPublishDateDesc<T extends { publish_date: string | null; title: string }>(
  rows: readonly T[],
): T[] {
  return rows
    .map((row) => ({ row, t: publishTime(row.publish_date) }))
    .sort((a, b) => {
      if (a.t !== b.t) {
        if (a.t === null) return 1;
        if (b.t === null) return -1;
        return b.t - a.t;
      }
      return a.row.title.localeCompare(b.row.title, undefined, {
        sensitivity: "base",
        numeric: true,
      });
    })
    .map(({ row }) => row);
}

/** Cents →"$12,500.00" in the product's own currency. */
export function formatPrice(cents: number, currency: string): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currency || "USD",
  }).format(cents / 100);
}
